/* Color spectrum picker for the product page.
   A slim strip of every color. The color under the cursor magnifies (neighbours a little, like the
   Mac dock) and a card floats above it with that color's photo and price. Click / tap selects.
   Selection goes through the real radio inputs, so ll-product handles price, stock, the main
   gallery and the URL exactly as before. */
(function () {
  'use strict';
  if (customElements.get('ll-color-spectrum')) return;

  // Used only when a color has no swatch color in Shopify and none in the theme setting.
  const NAMED = {
    black: '#141414', white: '#f4f4f4', gray: '#808080', grey: '#808080', silver: '#c0c0c0',
    beige: '#ffffff', cream: '#f3ead3', brown: '#7f520e', coffee: '#7f520e', red: '#e53935',
    orange: '#ff8a00', yellow: '#ffd600', gold: '#d4a017', green: '#43a047', matcha: '#b3e8a3',
    teal: '#1aa39a', blue: '#1e66d0', navy: '#1b2a59', purple: '#8e44ad', pink: '#f48fb1',
    sakura: '#f6ccf3', dandelion: '#fefba9', clear: '#e6eef2', transparent: '#e6eef2'
  };
  const WHITE = '#ffffff'; // same white as the White swatch on the other products
  const REACH = 2.2;   // how many neighbours on each side feel the magnification
  const GROW = 1.6;    // extra width for the hovered color (1.6 = 2.6x wide)

  function normHex(h) {
    if (!h) return '';
    h = String(h).trim();
    if (/^#?[0-9a-f]{3}$/i.test(h)) h = h.replace(/^#?(.)(.)(.)$/, '#$1$1$2$2$3$3');
    if (/^[0-9a-f]{6}$/i.test(h)) h = '#' + h;
    return /^#[0-9a-f]{6}$/i.test(h) ? h.toLowerCase() : '';
  }
  function hsl(hex) {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
    let h = 0, s = 0;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h *= 60;
    }
    return { h, s, l };
  }

  class LlColorSpectrum extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this.stage = this.querySelector('.spectrum__stage');
      this.bar = this.querySelector('.spectrum__bar');
      this.card = this.querySelector('.spectrum__card');
      this.cardImg = this.card.querySelector('img');
      this.cardPhoto = this.card.querySelector('.spectrum__photo');
      this.cardName = this.card.querySelector('.spectrum__name');
      this.cardPrice = this.card.querySelector('.spectrum__price');
      const product = this.closest('ll-product');
      this.soldOutText = (product && product.dataset.soldOutText) || 'Sold out';

      this.items = Array.from(this.querySelectorAll('input[data-option]')).map((input) => {
        const key = input.value.trim().toLowerCase();
        const hex = normHex(input.dataset.hex) || NAMED[key] || NAMED[key.split(' ').pop()] || '#8a8f98';
        return { input, name: input.value, hex, photo: input.dataset.photo, price: input.dataset.price };
      });
      if (this.items.length < 2) return;

      // Beige filament is really white, so show it with the same white used for White.
      this.items.forEach((it) => { if (it.name.trim().toLowerCase() === 'beige') it.hex = WHITE; });

      this.items.forEach((it) => { const c = hsl(it.hex); it.c = { ...c, neutral: c.s < 0.2 || c.l < 0.1 || c.l > 0.93 }; });
      if (this.dataset.sort === 'true') {
        // Rainbow order for real colors, then neutrals from dark to light.
        const hueKey = (h) => (h - 345 + 360) % 360; // reds first
        this.items.sort((a, b) => {
          if (a.c.neutral !== b.c.neutral) return a.c.neutral ? 1 : -1;
          if (a.c.neutral) return a.c.l - b.c.l;
          return hueKey(a.c.h) - hueKey(b.c.h);
        });
      }

      this.items.forEach((it, order) => {
        it.order = order;
        const seg = document.createElement('span');
        seg.className = 'spectrum__seg';
        seg.style.setProperty('--c', it.hex);
        seg.style.setProperty('--i', order);
        if (it.c.l > 0.85) seg.classList.add('is-light');
        if (it.c.l < 0.12) seg.classList.add('is-dark');
        this.bar.appendChild(seg);
        it.seg = seg;
      });

      this.bind();
      this.sync();
      this.reveal();
    }

    bind() {
      let preloaded = false;
      const preload = () => {
        if (preloaded) return;
        preloaded = true;
        this.items.forEach((it) => { if (it.photo) { const im = new Image(); im.src = it.photo; } });
      };

      this.bar.addEventListener('pointerenter', preload);
      this.bar.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'touch') return;
        this.magnifyAt(e.clientX);
      });
      this.bar.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'touch') return;
        this.release();
      });
      this.bar.addEventListener('click', (e) => {
        const it = this.items.find((x) => x.seg === e.target.closest('.spectrum__seg'));
        if (!it) return;
        this.select(it);
        if (e.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) {
          // On touch screens: briefly show the card for the tapped color.
          preload();
          this.focusItem(it);
          clearTimeout(this._touchTimer);
          this._touchTimer = setTimeout(() => this.release(), 1600);
        }
      });

      this.items.forEach((it) => {
        it.input.addEventListener('focus', () => { it.seg.classList.add('is-focus'); this.focusItem(it); });
        it.input.addEventListener('blur', () => { it.seg.classList.remove('is-focus'); this.release(); });
        it.input.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const n = this.items.length;
          const next = this.items[(it.order + dir + n) % n];
          next.input.focus();
          this.select(next);
        });
      });
      // ll-product updates availability after the change event reaches it, so sync a tick later.
      this.addEventListener('change', () => setTimeout(() => this.sync(), 0));
    }

    // Dock-style magnification around a point on the bar.
    magnifyAt(clientX) {
      const segs = this.items.map((it) => it.seg);
      const rects = segs.map((s) => s.getBoundingClientRect());
      // Work in "slot" units so the effect doesn't depend on the current (already magnified) widths.
      let pos = 0;
      for (let k = 0; k < rects.length; k++) {
        const r = rects[k];
        if (clientX >= r.left && clientX <= r.right) { pos = k + (clientX - r.left) / r.width; break; }
        if (clientX > r.right) pos = k + 1;
      }
      let hot = null, best = -1;
      this.items.forEach((it, k) => {
        const d = Math.abs(pos - (k + 0.5));
        const f = Math.max(0, 1 - d / REACH);
        const ease = f * f * (3 - 2 * f);
        it.seg.style.setProperty('--f', ease.toFixed(3));
        if (ease > best) { best = ease; hot = it; }
      });
      this.classList.add('is-hovering');
      if (hot) this.showCard(hot);
    }

    focusItem(it) {
      this.items.forEach((x) => {
        const d = Math.abs(x.order - it.order);
        const f = Math.max(0, 1 - d / REACH);
        x.seg.style.setProperty('--f', (f * f * (3 - 2 * f)).toFixed(3));
      });
      this.classList.add('is-hovering');
      this.showCard(it);
    }

    release() {
      this.items.forEach((it) => it.seg.style.setProperty('--f', 0));
      this.classList.remove('is-hovering');
      this.hot = null;
      this.items.forEach((x) => x.seg.classList.remove('is-hot'));
    }

    showCard(it) {
      if (this.hot !== it) {
        this.hot = it;
        this.items.forEach((x) => x.seg.classList.toggle('is-hot', x === it));
        const soldOut = it.input.classList.contains('is-unavailable');
        this.cardName.textContent = it.name;
        this.cardPrice.textContent = soldOut ? this.soldOutText : (it.price || '');
        this.cardPrice.classList.toggle('is-out', soldOut);
        this.card.style.setProperty('--c', it.hex);
        if (it.photo) {
          this.cardPhoto.classList.remove('is-swatch');
          if (this.cardImg.getAttribute('src') !== it.photo) {
            this.cardImg.classList.remove('is-loaded');
            this.cardImg.onload = () => this.cardImg.classList.add('is-loaded');
            this.cardImg.src = it.photo;
            if (this.cardImg.complete) this.cardImg.classList.add('is-loaded');
          }
        } else {
          this.cardPhoto.classList.add('is-swatch');
        }
      }
      // Place the card over the hot color, kept inside the picker.
      requestAnimationFrame(() => {
        const stage = this.stage.getBoundingClientRect();
        const seg = it.seg.getBoundingClientRect();
        const w = this.card.offsetWidth;
        const centre = seg.left + seg.width / 2 - stage.left;
        const left = Math.min(Math.max(centre - w / 2, 0), stage.width - w);
        this.card.style.setProperty('--x', left + 'px');
        this.card.style.setProperty('--arrow', (centre - left) + 'px');
      });
    }

    select(it) {
      if (!it.input.checked) {
        it.input.checked = true;
        it.input.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.sync();
      }
    }

    sync() {
      const sel = this.items.find((it) => it.input.checked);
      this.items.forEach((it) => {
        it.seg.classList.toggle('is-selected', it === sel);
        it.seg.classList.toggle('is-soldout', it.input.classList.contains('is-unavailable'));
      });
    }

    reveal() {
      if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('is-ready');
        return;
      }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this.classList.add('is-ready'); io.disconnect(); }
      }, { threshold: 0.4 });
      io.observe(this);
    }
  }

  customElements.define('ll-color-spectrum', LlColorSpectrum);
})();
